import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { LuUsers, LuPlus, LuPencil, LuTrash2, LuSave, LuX, LuEye, LuUserPlus, LuInfo } from 'react-icons/lu'
import api from '../services/api'
import './UsuariosPage.css'

function UsuariosPage() {
  const { isOperator, roleReady, portalRole } = useAuth()
  const canManageUsers = isOperator || portalRole === 'administrador'
  
  // Estado para lista de usuarios
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  // Estado para modal de crear/editar usuario
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState('create') // 'create' | 'edit' | 'view'
  const [selectedUser, setSelectedUser] = useState(null)
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    portal_role: 'principal',
    client_id: '',
    accounts: []
  })
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState('')
  const [showRoleTooltip, setShowRoleTooltip] = useState(false)

  // Clientes (multi-tenant) — requeridos para crear nuevos usuarios CREDENCIAL
  const [clients, setClients] = useState([])
  const [clientsLoading, setClientsLoading] = useState(false)

  // Cargar usuarios al montar. El admin de cliente también entra acá;
  // el listado lo recorta el backend por su client_id.
  useEffect(() => {
    if (!roleReady || !canManageUsers) return
    loadUsers()
    if (isOperator) loadClients()
  }, [roleReady, canManageUsers, isOperator])

  const loadClients = async () => {
    try {
      setClientsLoading(true)
      const response = await api.get('/clients', { params: { include_empty: true } })
      const all = response.data?.clients || []
      const credencialClients = all.filter(c => (c.bank_role || '').toUpperCase() === 'CREDENCIAL')
      setClients(credencialClients)
    } catch (err) {
      console.error('Error cargando clientes:', err)
    } finally {
      setClientsLoading(false)
    }
  }

  const loadUsers = async () => {
    try {
      setLoading(true)
      const response = await api.get('/users')
      setUsers(response.data.users || [])
    } catch (err) {
      setError('Error al cargar usuarios')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setModalMode('create')
    setFormData({
      username: '',
      password: '',
      full_name: '',
      email: '',
      portal_role: 'principal',
      client_id: '',
      accounts: [{ cvu: '', account_number: '', alias: '' }]
    })
    setFormError('')
    setShowModal(true)
  }

  const openEditModal = async (user) => {
    try {
      setFormLoading(true)
      const response = await api.get(`/users/${user.id}`)
      const userData = response.data.user
      
      setModalMode('edit')
      setSelectedUser(userData)
      setFormData({
        username: userData.username,
        password: '', // No mostrar contraseña
        full_name: userData.full_name || '',
        email: userData.email || '',
        portal_role: userData.portal_role || 'principal',
        client_id: userData.client_id != null ? String(userData.client_id) : '',
        accounts: userData.accounts.length > 0 
          ? userData.accounts 
          : [{ cvu: '', account_number: '', alias: '' }]
      })
      setFormError('')
      setShowModal(true)
    } catch (err) {
      setError('Error al cargar usuario')
    } finally {
      setFormLoading(false)
    }
  }

  const openViewModal = async (user) => {
    try {
      setFormLoading(true)
      const response = await api.get(`/users/${user.id}`)
      const userData = response.data.user
      
      setModalMode('view')
      setSelectedUser(userData)
      setFormData({
        username: userData.username,
        password: '',
        full_name: userData.full_name || '',
        email: userData.email || '',
        portal_role: userData.portal_role || 'principal',
        client_id: userData.client_id != null ? String(userData.client_id) : '',
        accounts: userData.accounts
      })
      setShowModal(true)
    } catch (err) {
      setError('Error al cargar usuario')
    } finally {
      setFormLoading(false)
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedUser(null)
    setFormData({
      username: '',
      password: '',
      full_name: '',
      email: '',
      portal_role: 'principal',
      client_id: '',
      accounts: []
    })
    setFormError('')
    setShowRoleTooltip(false)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleAccountChange = (index, field, value) => {
    const newAccounts = [...formData.accounts]
    newAccounts[index] = { ...newAccounts[index], [field]: value }
    setFormData(prev => ({ ...prev, accounts: newAccounts }))
  }

  const addAccount = () => {
    setFormData(prev => ({
      ...prev,
      accounts: [...prev.accounts, { cvu: '', account_number: '', alias: '' }]
    }))
  }

  const removeAccount = (index) => {
    if (formData.accounts.length > 1) {
      const newAccounts = formData.accounts.filter((_, i) => i !== index)
      setFormData(prev => ({ ...prev, accounts: newAccounts }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormLoading(true)
    setFormError('')

    try {
      // Filtrar cuentas vacías
      const validAccounts = formData.accounts.filter(
        acc => acc.cvu && acc.account_number
      )

      if (modalMode === 'create') {
        if ((formData.username || '').trim().length < 8) {
          setFormError('El usuario debe tener al menos 8 caracteres')
          setFormLoading(false)
          return
        }
        if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{8,}$/.test(formData.password || '')) {
          setFormError('La contraseña debe tener mínimo 8 caracteres, 1 mayúscula, 1 minúscula y 1 especial')
          setFormLoading(false)
          return
        }

        const payload = {
          username: formData.username,
          password: formData.password,
          full_name: formData.full_name,
          email: formData.email,
          portal_role: formData.portal_role,
          accounts: validAccounts
        }

        // El operador global elige cliente. El admin de cliente lo toma la sesión.
        if (isOperator) {
          const clientIdInt = parseInt(formData.client_id, 10)
          if (!Number.isFinite(clientIdInt) || clientIdInt < 1) {
            setFormError('Debés seleccionar un cliente para el nuevo usuario')
            setFormLoading(false)
            return
          }
          payload.client_id = clientIdInt
        }

        await api.post('/users', payload)
        setSuccess('Usuario creado exitosamente')
      } else if (modalMode === 'edit') {
        // Actualizar datos del usuario
        await api.put(`/users/${selectedUser.id}`, {
          username: formData.username,
          full_name: formData.full_name,
          email: formData.email,
          portal_role: formData.portal_role,
          password: formData.password || undefined
        })

        // Manejar cuentas
        const existingAccountIds = selectedUser.accounts.map(a => a.id)
        const newAccountIds = formData.accounts.filter(a => a.id).map(a => a.id)
        
        // Eliminar cuentas que ya no están
        for (const acc of selectedUser.accounts) {
          if (!newAccountIds.includes(acc.id)) {
            await api.delete(`/users/${selectedUser.id}/accounts/${acc.id}`)
          }
        }
        
        // Agregar cuentas nuevas
        for (const acc of validAccounts) {
          if (!acc.id) {
            await api.post(`/users/${selectedUser.id}/accounts`, acc)
          }
        }

        setSuccess('Usuario actualizado exitosamente')
      }

      closeModal()
      loadUsers()
    } catch (err) {
      setFormError(err.response?.data?.error || 'Error al guardar usuario')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`¿Está seguro de eliminar al usuario "${user.username}"? Esta acción no se puede deshacer.`)) {
      return
    }

    try {
      await api.delete(`/users/${user.id}`)
      setSuccess('Usuario eliminado exitosamente')
      loadUsers()
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar usuario')
    }
  }

  // Limpiar mensajes después de 5 segundos
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('')
        setError('')
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [success, error])

  if (!roleReady || !canManageUsers) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <p className="empty-state-large">Acceso denegado. Solo el operador o un administrador de cliente puede gestionar usuarios.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="page-container">
      <div className="page-header">
            <h1><LuUsers /> Administrar Usuarios</h1>
            <button className="btn-primary btn-nuevo-usuario" onClick={openCreateModal}>
              <LuUserPlus /> <span>Nuevo Usuario</span>
            </button>
          </div>

      {/* Mensajes */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Lista de usuarios */}
      {loading ? (
        <div className="loading-state">Cargando usuarios...</div>
      ) : users.length === 0 ? (
        <div className="empty-state">
          <p>No hay usuarios registrados.</p>
          <p>Haga clic en "Nuevo Usuario" para crear el primero.</p>
        </div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Nombre</th>
                <th>Email</th>
                <th>Rol</th>
                <th>CVUs Asignados</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td className="username-cell">{user.username}</td>
                  <td>{user.full_name || '-'}</td>
                  <td>{user.email || '-'}</td>
                  <td>
                    <span className={`role-badge ${user.portal_role === 'lector' ? 'role-lector' : 'role-principal'}`}>
                      {user.portal_role === 'lector' ? 'Lector' : user.portal_role === 'secundario' ? 'Secundario' : user.portal_role === 'administrador' ? 'Administrador' : 'Principal'}
                    </span>
                  </td>
                  <td className="account-count">{user.account_count}</td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button className="btn-icon btn-view" onClick={() => openViewModal(user)} title="Ver detalle">
                      <LuEye />
                    </button>
                    <button className="btn-icon btn-edit" onClick={() => openEditModal(user)} title="Editar">
                      <LuPencil />
                    </button>
                    <button className="btn-icon btn-delete" onClick={() => handleDeleteUser(user)} title="Eliminar">
                      <LuTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de Usuario */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content user-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {modalMode === 'create' && 'Nuevo Usuario'}
                {modalMode === 'edit' && 'Editar Usuario'}
                {modalMode === 'view' && 'Detalle de Usuario'}
              </h2>
              <button className="btn-close" onClick={closeModal}>
                <LuX />
              </button>
            </div>

            {formError && <div className="alert alert-error">{formError}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h3>Datos del Usuario</h3>
                <div className="form-grid">
                  {modalMode === 'create' && isOperator && (
                    <div className="form-group form-group-full">
                      <label>Cliente *</label>
                      <select
                        name="client_id"
                        value={formData.client_id}
                        onChange={handleInputChange}
                        disabled={clientsLoading}
                        className="role-select"
                        required
                      >
                        <option value="">
                          {clientsLoading ? 'Cargando clientes...' : 'Seleccionar cliente...'}
                        </option>
                        {clients.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.name}{c.slug ? ` · ${c.slug}` : ''}
                          </option>
                        ))}
                      </select>
                      {!clientsLoading && clients.length === 0 && (
                        <small className="form-hint">No hay clientes CREDENCIAL disponibles.</small>
                      )}
                    </div>
                  )}
                  {modalMode !== 'create' && formData.client_id && (
                    <div className="form-group form-group-full">
                      <label>Cliente</label>
                      <input
                        type="text"
                        value={(clients.find(c => String(c.id) === String(formData.client_id))?.name) || `ID ${formData.client_id}`}
                        disabled
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Usuario *</label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      placeholder="nombre_usuario"
                      disabled={modalMode === 'view'}
                      required
                    />
                  </div>
                  {modalMode !== 'view' && (
                    <div className="form-group">
                      <label>{modalMode === 'create' ? 'Contraseña *' : 'Nueva Contraseña'}</label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder={modalMode === 'edit' ? 'Dejar vacío para mantener' : '••••••••'}
                        required={modalMode === 'create'}
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Nombre Completo</label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      placeholder="Juan Pérez"
                      disabled={modalMode === 'view'}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="email@ejemplo.com"
                      disabled={modalMode === 'view'}
                    />
                  </div>
                  <div className="form-group">
                    <label className="label-with-tooltip">
                      Rol
                      <button 
                        type="button" 
                        className="tooltip-trigger"
                        onClick={() => setShowRoleTooltip(!showRoleTooltip)}
                        onMouseEnter={() => setShowRoleTooltip(true)}
                        onMouseLeave={() => setShowRoleTooltip(false)}
                      >
                        <LuInfo />
                      </button>
                      {showRoleTooltip && (
                        <div className="role-tooltip">
                          <strong>Principal:</strong> Uso normal de la cuenta, incluyendo transferencias.
                          <br /><br />
                          <strong>Secundario:</strong> Operación de la cuenta con permisos intermedios.
                          <br /><br />
                          <strong>Lector:</strong> Solo consulta saldos y movimientos. No transfiere.
                        </div>
                      )}
                    </label>
                    <select
                      name="portal_role"
                      value={formData.portal_role}
                      onChange={handleInputChange}
                      disabled={modalMode === 'view'}
                      className="role-select"
                    >
                      <option value="principal">Principal</option>
                      <option value="secundario">Secundario</option>
                      <option value="lector">Lector</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="section-header">
                  <h3>CVUs Asignados</h3>
                  {modalMode !== 'view' && (
                    <button type="button" className="btn-add-account" onClick={addAccount}>
                      <LuPlus /> Agregar CVU
                    </button>
                  )}
                </div>

                {formData.accounts.length === 0 ? (
                  <p className="no-accounts">No hay CVUs asignados</p>
                ) : (
                  <div className="accounts-list">
                    {formData.accounts.map((account, index) => (
                      <div key={index} className="account-row">
                        <div className="account-fields">
                          <div className="form-group">
                            <label>CVU (22 dígitos) *</label>
                            <input
                              type="text"
                              value={account.cvu}
                              onChange={(e) => handleAccountChange(index, 'cvu', e.target.value)}
                              placeholder="0000000000000000000000"
                              maxLength={22}
                              disabled={modalMode === 'view'}
                            />
                          </div>
                          <div className="form-group">
                            <label>N° de Cuenta *</label>
                            <input
                              type="text"
                              value={account.account_number}
                              onChange={(e) => handleAccountChange(index, 'account_number', e.target.value)}
                              placeholder="123456789"
                              disabled={modalMode === 'view'}
                            />
                          </div>
                          <div className="form-group">
                            <label>Alias</label>
                            <input
                              type="text"
                              value={account.alias || ''}
                              onChange={(e) => handleAccountChange(index, 'alias', e.target.value)}
                              placeholder="mi.alias.cvu"
                              disabled={modalMode === 'view'}
                            />
                          </div>
                        </div>
                        {modalMode !== 'view' && formData.accounts.length > 1 && (
                          <button
                            type="button"
                            className="btn-remove-account"
                            onClick={() => removeAccount(index)}
                          >
                            <LuTrash2 />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {modalMode !== 'view' && (
                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary" disabled={formLoading}>
                    <LuSave /> {formLoading ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default UsuariosPage

